import React, { useState } from 'react';
import './DataTable.scss';
import DataTableService from './DataTableService';
import { Table } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Icon } from '../Icon';
import { IconType } from '../Icon/interfaces';

export interface ITableColumn {
  // Column header
  header? : string;

  // Field name
  field? : string;

  // Customer class for individual column header
  headerClassName? : string;

  // Column width
  width? : string;

    maxWidth?: string;
  // handling tabbing
  tabIndex?: number;

  // Class name for cell TD 
  cellTdClassName? : string;

  // Class name for cell td content
  cellTdContentClassName? : string;

  cellDataFormatter? : (cellData : any) => any;

    cellTemplate?: (rowData : any, column : ITableColumn, rowIndex : number, dataTableState? : DataTableState, cellTemplateCallBackHanlder?: (dataTableEventArgs: DataTableEventArgs) => void) => JSX.Element;

  // cellTemplateActionHandler? : (dataTableEventArgs : DataTableEventArgs) => void;

    cellDataChangeHandler?: (dataTableEventArgs: DataTableEventArgs) =>CellDataChangeArgs;

    cellClickHandler?: (dataTableEventArgs: DataTableEventArgs) =>void;

    onKeyDownHandler?: (dataTableEventArgs?: DataTableEventArgs) => void;
}

export interface CellDataChangeArgs {
  dataTableRowsState? : DataTableRowsState
}

export interface DataTableEventArgs {
  rowData? : any;

  column? : ITableColumn;

  rowIndex? : number;

  dataTableState? : DataTableState;

  eventData? : any;
}

export interface ITable {
  // List of columns 
  columns : ITableColumn[];

  // Data to render in table.
  data : any[];

  keyColumnName? : string;

  tableClassName? : string

  // Class name for thead section
  theadClassName? : string

  // Default column width
  defaultColumnWidth? : string  

  /// Row detail template provider
  rowDetailTemplate? : (rowData : any, dataTableState : DataTableState, rowIndex : number) => JSX.Element; 

  isEditDisabled? : boolean

  isDeleteDisabled? : boolean

  renderRow? :any

  expandedRowId?: string | null;

  setExpandedRowId?: (id: string | null) => void;

  isFormSaved?: boolean; 
};

export interface RowState {
  expanded? : boolean
}

export interface DataTableRowsState {
  [rowId: string]: RowState; 
}

export interface DataTableState {
  dataTable : ITable;
  rowsState : DataTableRowsState
}

export class DataTableDefaultCellTemplates {
static RowExpander = {
    CellTemplate: (rowData: any, column: ITableColumn, rowIndex: number, dataTableState?: DataTableState,
        cellTemplateCallBackHandler?: (dataTableEventArgs: DataTableEventArgs) => void) => {
        const isRowExpanded = rowData && dataTableState?.dataTable.expandedRowId === rowData[dataTableState?.dataTable.keyColumnName];
        const isAnotherRowExpanded = dataTableState?.dataTable.expandedRowId && !isRowExpanded;

        const dataEventArgs: DataTableEventArgs = {
            rowData, column, rowIndex, dataTableState, eventData: {}
        };

        return isRowExpanded ? (
            <FaChevronDown
                tabIndex={column.tabIndex != null ? column.tabIndex : undefined}
                className={`cl-expander-icon ${isAnotherRowExpanded ? 'chevron-disabled' : ''}`}
                // Replace the problematic line with a proper KeyboardEvent handler function
                onKeyDown={e => {
                    if (column?.onKeyDownHandler) {
                        column.onKeyDownHandler({
                            rowData,
                            column,
                            rowIndex,
                            dataTableState,
                            eventData: e
                        });
                    }
                }}
                onClick={() => {
                    dataTableState?.dataTable.setExpandedRowId(null); // Collapse the current row
                    column?.cellClickHandler?.({ ...dataEventArgs, eventData: { expand: false } });
                }}
            />
        ) : (
            <FaChevronRight
                    tabIndex={column.tabIndex != null ? column.tabIndex : undefined}
                    className={`cl-expander-icon ${isAnotherRowExpanded ? 'chevron-disabled' : ''}`}
                    onKeyDown={e => {
                        if (column?.onKeyDownHandler) {
                            column.onKeyDownHandler({
                                rowData,
                                column,
                                rowIndex,
                                dataTableState,
                                eventData: e
                            });
                        }
                    }}
                    onClick={() => {
                    dataTableState?.dataTable.setExpandedRowId(rowData[dataTableState?.dataTable.keyColumnName]); // Expand the clicked row
                    column?.cellClickHandler?.({ ...dataEventArgs, eventData: { expand: true } });
                }}
            />
        );
    }
};
    static EditCellTemplate = {
      CellTemplate : (rowData : any, column : ITableColumn, rowIndex : number, dataTableState? : DataTableState) => {
            const dtEventArgs : DataTableEventArgs =  { rowData,  column, rowIndex, dataTableState, eventData : null };
            const isEditDisabled = dataTableState.dataTable.isEditDisabled;
            const iconClass = isEditDisabled ? 'disabled-icon' : ''
            return <Icon icon={'edit' as IconType} onClick={() => !isEditDisabled && column?.cellClickHandler(dtEventArgs)} className={iconClass} />;
      }
    };
    static DeleteCellTemplate = {
        CellTemplate: (rowData: any, column: ITableColumn, rowIndex: number, dataTableState?: DataTableState) => {
            const dtEventArgs : DataTableEventArgs =  { rowData,  column, rowIndex, dataTableState, eventData : null };
            const isDeleteDisabled = dataTableState.dataTable.isDeleteDisabled;
            const iconClass = dataTableState.dataTable.expandedRowId == rowData.id ? isDeleteDisabled ? 'disabled-icon' : '' : 'disabled-icon'
            return <div tabIndex={column.tabIndex} 
                onKeyDown={e => {
                    if (column?.onKeyDownHandler) {
                        column.onKeyDownHandler({
                            rowData,
                            column,
                            rowIndex,
                            dataTableState,
                            eventData: e
                        });
                    }
                }}
            >
                <Icon icon={'trash' as IconType} onClick={() => !isDeleteDisabled && column?.cellClickHandler(dtEventArgs)} className={iconClass} />
            </div>
      }
    };
};

const DataTable : React.FunctionComponent<ITable> = (props : ITable) => {
  const tableService = new DataTableService();

  const { columns
        , data
        , theadClassName
        , defaultColumnWidth
        , tableClassName
        , isEditDisabled
        , isDeleteDisabled
        , renderRow
      } = props;

  
  const [rowsState, setRowsState ] = useState<any>({});

  const cellTemplateActionHanlder = (dataTableEventArgs : DataTableEventArgs) => {
    let updatedRowsState = (dataTableEventArgs.column.cellDataChangeHandler(dataTableEventArgs));
    setRowsState(updatedRowsState.dataTableRowsState);
  }

  const columnContent = tableService.getTableHeader(columns, defaultColumnWidth);

  const tableClass = tableService.getClassName(tableClassName, 'tbl-main');

  const theadClass = tableService.getClassName(theadClassName, 'tblHeader');

  let dataTableState : DataTableState = {dataTable : props, rowsState : rowsState};
  const bodyContent = tableService.getTableBody(columns, data, dataTableState, cellTemplateActionHanlder, renderRow);

  return (
    <div>
      <Table className={tableClass}>
        <thead className={theadClass}>
          <tr>
            {columnContent}
          </tr>
        </thead>
        <tbody>
          {bodyContent}
        </tbody>
      </Table>
    </div>
  );
}

export default DataTable;